import { observable, computed, action, toJS } from 'mobx';
import { AxiosResponse } from 'axios';

import { JrpcResponse } from '@httpClient/jrpc';
import Services from '@services/index';
import {
  LoadService,
  ServiceFilterProps,
  Service,
  ServiceInterface,
  ServiceKeys,
} from '@entities/Billing/Service';
import Store from './Store';
import { Loading, endLoading } from '@stores/interfaces/Loading';
import { Entity } from '@stores/interfaces/Entity';
import { Pagination, SetLimit, SetOffset } from './interfaces/Pagination';
import { Filter } from './interfaces/Filter';

type ServiceIndexResponse = JrpcResponse<{ items: ServiceInterface[]; total: number }>;

class ServiceStore extends Store
  implements
    Loading,
    Pagination,
    Filter<ServiceFilterProps, ServiceKeys>,
    Entity<Service, { filter?: ServiceFilterProps; limit?: number; offset?: number }> {
  @observable private _serviceValues: Service[];
  @observable private _filter: ServiceFilterProps;
  @observable private _loading: boolean;
  @observable private _limit: number;
  @observable private _offset: number;
  @observable private _total: number;

  @action private _endLoading = endLoading(50).bind(this);

  public constructor(services: Services) {
    super(services);

    this._serviceValues = [];
    this._filter = {};
    this._loading = false;
    this._limit = 0;
    this._offset = 0;
    this._total = 0;
  }

  @action public load: LoadService = async params => {
    let services: Service[] = [];

    this._loading = true;

    const filter = params?.filter || toJS(this._filter);
    const limit = params && typeof params.limit === 'number' ? params.limit : this._limit;
    const offset = params && typeof params.offset === 'number' ? params.offset : this._offset;

    this._filter = filter;
    this._limit = limit;
    this._offset = offset;

    await this._services.billing.requests
      .serviceIndex({ params: { filter, limit, offset } })
      .then(({ data: { result } }: AxiosResponse<ServiceIndexResponse>) => {
        if (result?.items && Array.isArray(result.items)) {
          services = result.items.map(value => new Service(value));

          this._serviceValues = services;
        }

        if (result?.total && typeof result.total === 'number') {
          this._total = result.total;
        }
      });

    return services;
  };

  @action public setLimit: SetLimit = limit => {
    this._limit = limit;
  };

  @action public setOffset: SetOffset = offset => {
    this._offset = offset;
  };

  @action public cleanUp = (): void => {
    this._serviceValues = [];
    this._filter = {};
    this._loading = false;
    this._limit = 0;
    this._offset = 0;
    this._total = 0;
  };

  @computed public get list(): Service[] {
    return toJS(this._serviceValues);
  }

  @computed public get filter(): ServiceFilterProps {
    return toJS(this._filter);
  }

  @computed public get loading(): boolean {
    return this._loading;
  }

  @computed public get limit(): number {
    return this._limit;
  }

  @computed public get offset(): number {
    return this._offset;
  }

  @computed public get total(): number {
    return this._total;
  }
}

export default ServiceStore;
